import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import { exportExcel, exportPDF, exportCSV, printReport } from "../lib/export";
import {
  ArrowLeft, Building2, FileText, FileDown, FileSpreadsheet, Printer,
  Home, Wrench, Banknote, TrendingUp, Filter, X, BarChart3,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const PROPERTY_REPORTS = [
  { key: "income-statement", label: "Income Statement (P&L)" },
  { key: "balance-sheet", label: "Balance Sheet" },
  { key: "cash-flow", label: "Cash Flow" },
  { key: "rent-roll", label: "Rent Roll" },
  { key: "expenses-by-category", label: "Expense Report" },
  { key: "property-performance", label: "Property Performance" },
  { key: "bank-activity", label: "Bank / Zelle Activity" },
  { key: "security-deposit", label: "Security Deposit Liability" },
];

// Reports whose data is filtered by a date range (others are point-in-time snapshots).
const DATE_DRIVEN = new Set(["income-statement", "cash-flow", "expenses-by-category", "bank-activity"]);

const fmtCell = (v, col) => {
  if (col.money) return money(v);
  if (["percent", "cap_rate", "roi", "occupancy"].includes(col.key)) return `${v}%`;
  return v ?? "—";
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [overview, setOverview] = useState(null);
  const [reportKey, setReportKey] = useState("income-statement");
  const [report, setReport] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadOverview = useCallback(async () => {
    try {
      const [props, units, leases, mortgages, maint] = await Promise.all([
        api.get("/properties"), api.get("/units"), api.get("/leases"),
        api.get("/mortgages"), api.get("/maintenance"),
      ]);
      const prop = props.data.find((p) => p.id === id);
      setProperty(prop || null);
      const pUnits = units.data.filter((u) => u.property_id === id);
      const pLeases = leases.data.filter((l) => l.property_id === id && l.status === "Active");
      const pMort = mortgages.data.filter((m) => m.property_id === id);
      const openMaint = maint.data.filter(
        (m) => m.property_id === id && !["Completed", "Closed", "Cancelled"].includes(m.status)
      );
      const currentRent = pLeases.reduce((s, l) => s + Number(l.monthly_rent || 0), 0);
      const marketRent = pUnits.reduce((s, u) => s + Number(u.market_rent || 0), 0);
      const mortgageBalance = pMort.reduce((s, m) => s + Number(m.current_balance || m.balance || 0), 0)
        || Number(prop?.mortgage_balance || 0);
      setOverview({
        unitCount: pUnits.length || Number(prop?.units || 0),
        activeLeases: pLeases.length,
        currentRent, marketRent,
        mortgageBalance, openMaint: openMaint.length,
        marketValue: Number(prop?.market_value || 0),
      });
    } catch (e) {
      console.error("Property overview load failed:", e);
    }
  }, [id]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const showDateFilter = DATE_DRIVEN.has(reportKey);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    p.append("property_id", id);
    if (DATE_DRIVEN.has(reportKey)) {
      if (dateFrom) p.append("date_from", dateFrom);
      if (dateTo) p.append("date_to", dateTo);
    }
    return `?${p.toString()}`;
  }, [id, reportKey, dateFrom, dateTo]);

  useEffect(() => {
    setReport(null);
    api.get(`/reports/${reportKey}${qs()}`).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [reportKey, qs]);

  const subtitle = property ? `${property.name}${property.address ? " · " + property.address : ""}` : "";

  const doExport = (kind) => {
    if (!report) return;
    const cols = report.columns;
    const fname = `${(property?.name || "property").replace(/\s+/g, "-").toLowerCase()}-${report.title.replace(/\s+/g, "-").toLowerCase()}`;
    if (kind === "excel") exportExcel(fname, cols, report.rows);
    else if (kind === "csv") exportCSV(fname, cols, report.rows);
    else if (kind === "print") printReport(report.title, cols, report.rows, subtitle);
    else exportPDF(`${report.title} — ${property?.name || ""}`, cols, report.rows);
  };

  const clearDates = () => { setDateFrom(""); setDateTo(""); };
  const hasDates = dateFrom || dateTo;

  const stats = useMemo(() => {
    if (!overview) return [];
    const underMarket = overview.marketRent > 0 ? overview.marketRent - overview.currentRent : 0;
    return [
      { label: "Units", value: overview.unitCount, icon: Home, accent: "bg-blue-50 text-blue-600 dark:bg-blue-500/10" },
      { label: "Active Leases", value: overview.activeLeases, icon: FileText, accent: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" },
      { label: "Current Rent / mo", value: money(overview.currentRent), icon: TrendingUp, accent: "bg-violet-50 text-violet-600 dark:bg-violet-500/10" },
      { label: "Market Rent / mo", value: overview.marketRent > 0 ? money(overview.marketRent) : "—", icon: BarChart3, accent: "bg-amber-50 text-amber-600 dark:bg-amber-500/10", sub: underMarket > 0 ? `${money(underMarket)} under market` : null },
      { label: "Mortgage Balance", value: money(overview.mortgageBalance), icon: Banknote, accent: "bg-red-50 text-red-600 dark:bg-red-500/10" },
      { label: "Open Maintenance", value: overview.openMaint, icon: Wrench, accent: "bg-orange-50 text-orange-600 dark:bg-orange-500/10" },
    ];
  }, [overview]);

  return (
    <div className="space-y-6" data-testid="page-property-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate("/properties")} data-testid="back-to-properties" className="mt-1 text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Property Profile</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-primary" /> {property?.name || "Property"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{property?.address || ""}{property?.type ? ` · ${property.type}` : ""}</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="property-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Financial Reports</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          {property?.photo && (
            <div className="rounded-2xl overflow-hidden border border-border h-56 bg-muted">
              <img src={property.photo} alt={property.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {stats.map((s, i) => (
              <div key={s.label} className="rounded-2xl border border-border bg-card p-4" data-testid={`overview-stat-${i}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${s.accent}`}><s.icon className="w-4.5 h-4.5" strokeWidth={1.75} /></div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</p>
                <p className="font-heading text-lg font-bold tracking-tight mt-0.5">{s.value}</p>
                {s.sub && <p className="text-[11px] text-red-600 mt-0.5">{s.sub}</p>}
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            <p>Use the <button onClick={() => document.querySelector('[data-testid="tab-reports"]')?.click()} className="text-primary font-medium hover:underline">Financial Reports</button> tab to view this property's Income Statement, Balance Sheet, Cash Flow, Rent Roll, Expenses, Performance, Bank/Zelle Activity and Security Deposit Liability — all scoped to {property?.name || "this property"} with print and PDF/Excel/CSV export.</p>
          </div>
        </TabsContent>

        {/* Financial Reports */}
        <TabsContent value="reports" className="mt-5 space-y-5">
          {showDateFilter && (
          <section className="rounded-2xl border border-border bg-card p-4" data-testid="property-report-filter-bar">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm font-medium mr-2"><Filter className="w-4 h-4 text-primary" /> Date range</div>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" className="rounded-lg border border-border bg-background p-2 text-sm" />
              <span className="text-muted-foreground text-sm">to</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" className="rounded-lg border border-border bg-background p-2 text-sm" />
              {hasDates && <button onClick={clearDates} data-testid="report-clear-dates" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /> Clear</button>}
            </div>
          </section>
          )}

          <section className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <FileText className="w-4.5 h-4.5 text-primary" />
                <select value={reportKey} onChange={(e) => setReportKey(e.target.value)} data-testid="property-report-selector"
                  className="rounded-lg border border-border bg-background p-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30">
                  {PROPERTY_REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-xl" onClick={() => doExport("print")} data-testid="report-print"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
                <Button variant="outline" className="rounded-xl" onClick={() => doExport("csv")} data-testid="report-export-csv"><FileDown className="w-4 h-4 mr-1.5" /> CSV</Button>
                <Button variant="outline" className="rounded-xl" onClick={() => doExport("excel")} data-testid="report-export-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</Button>
                <Button className="rounded-xl" onClick={() => doExport("pdf")} data-testid="report-export-pdf"><FileDown className="w-4 h-4 mr-1.5" /> PDF</Button>
              </div>
            </div>

            {report ? (
              <>
                {report.summary?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-border bg-muted/30">
                    {report.summary.map((s) => (
                      <div key={s.label} data-testid={`property-report-summary-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</p>
                        <p className={`font-heading text-xl font-bold mt-0.5 ${s.negative ? "text-red-600" : ""}`}>{s.money ? money(s.value) : s.value}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="property-report-table">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>{report.columns.map((c) => <th key={c.key} className={`px-4 py-3 font-semibold ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {report.rows.length === 0 && <tr><td colSpan={report.columns.length} className="px-4 py-10 text-center text-muted-foreground">No data for this property{hasDates ? " in the selected range" : ""}.</td></tr>}
                      {report.rows.map((r, i) => (
                        <tr key={`${r[report.columns[0]?.key] ?? ""}-${i}`} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`property-report-row-${i}`}>
                          {report.columns.map((c) => (
                            <td key={c.key} className={`px-4 py-3 ${c.money ? "text-right font-mono" : ""} ${c.money && r[c.key] < 0 ? "text-red-600" : ""}`}>{fmtCell(r[c.key], c)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : <div className="p-10 text-center text-muted-foreground text-sm">Loading report…</div>}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
